export type RoleScope = 'workspace' | 'chat' | 'meeting';

export enum PermissionId {
  WorkspaceCreateChat = 'workspace_create_chat',
  WorkspaceCreateInstantMeeting = 'workspace_create_instant_meeting',
  WorkspaceCreateScheduledMeeting = 'workspace_create_scheduled_meeting',
  WorkspaceCreatePrivateRoom = 'workspace_create_private_room',
  WorkspaceInviteMembers = 'workspace_invite_members',
  WorkspaceRemoveMembers = 'workspace_remove_members',
  WorkspaceBanMembers = 'workspace_ban_members',
  WorkspaceManageRoles = 'workspace_manage_roles',
  WorkspaceEditSettings = 'workspace_edit_settings',
  WorkspaceCreateSubWorkspace = 'workspace_create_sub_workspace',
  WorkspaceDelete = 'workspace_delete',

  ChatInviteMembers = 'chat_invite_members',
  ChatCreateInstantCall = 'chat_create_instant_call',
  ChatScheduleMeeting = 'chat_schedule_meeting',
  ChatManageSettings = 'chat_manage_settings',
  ChatDeleteOthersMessages = 'chat_delete_others_messages',
  ChatRemoveMember = 'chat_remove_member',
  ChatBanMember = 'chat_ban_member',
  ChatDelete = 'chat_delete',
  ChatReassignManager = 'chat_reassign_manager',

  MeetingInviteUsers = 'meeting_invite_users',
  MeetingStartEarly = 'meeting_start_early',
  MeetingToggleWaitingList = 'meeting_toggle_waiting_list',
  MeetingEditDetails = 'meeting_edit_details',
  MeetingManageBreakoutRooms = 'meeting_manage_breakout_rooms',
  MeetingMuteParticipants = 'meeting_mute_participants',
  MeetingManageConferenceMode = 'meeting_manage_conference_mode',
  MeetingKickUsers = 'meeting_kick_users',
  MeetingBanUsers = 'meeting_ban_users',
  MeetingCancelScheduled = 'meeting_cancel_scheduled',
  MeetingReassignManager = 'meeting_reassign_manager',
}

export interface PermissionConfigEntry {
  id: PermissionId;
  scope: RoleScope;
  powerLevel: number;
  labelKey: string;
}

/**
 * MIP-035 permissions not yet backed by product UI. Kept in config for Matrix PL
 * thresholds and bot reconciliation; excluded from the role editor and counts.
 * Remove IDs one-by-one when each feature ships.
 */
export const HIDDEN_UI_PERMISSION_IDS: ReadonlySet<PermissionId> = new Set([
  PermissionId.WorkspaceBanMembers,
  PermissionId.ChatBanMember,
  PermissionId.ChatDelete,
  PermissionId.MeetingInviteUsers,
  PermissionId.MeetingStartEarly,
  PermissionId.MeetingToggleWaitingList,
  PermissionId.MeetingEditDetails,
  PermissionId.MeetingManageBreakoutRooms,
  PermissionId.MeetingMuteParticipants,
  PermissionId.MeetingManageConferenceMode,
  PermissionId.MeetingCancelScheduled,
  PermissionId.MeetingReassignManager,
]);

export const isPermissionVisibleInUi = (entry: PermissionConfigEntry): boolean =>
  !HIDDEN_UI_PERMISSION_IDS.has(entry.id);

export const workspacesConfig: Record<PermissionId, PermissionConfigEntry> = {
  [PermissionId.WorkspaceCreateChat]: {
    id: PermissionId.WorkspaceCreateChat,
    scope: 'workspace',
    powerLevel: 5,
    labelKey: 'workspace_roles.permission.workspace_create_chat.label',
  },
  [PermissionId.WorkspaceCreateInstantMeeting]: {
    id: PermissionId.WorkspaceCreateInstantMeeting,
    scope: 'workspace',
    powerLevel: 10,
    labelKey: 'workspace_roles.permission.workspace_create_instant_meeting.label',
  },
  [PermissionId.WorkspaceCreateScheduledMeeting]: {
    id: PermissionId.WorkspaceCreateScheduledMeeting,
    scope: 'workspace',
    powerLevel: 11,
    labelKey: 'workspace_roles.permission.workspace_create_scheduled_meeting.label',
  },
  [PermissionId.WorkspaceCreatePrivateRoom]: {
    id: PermissionId.WorkspaceCreatePrivateRoom,
    scope: 'workspace',
    powerLevel: 12,
    labelKey: 'workspace_roles.permission.workspace_create_private_room.label',
  },
  [PermissionId.WorkspaceInviteMembers]: {
    id: PermissionId.WorkspaceInviteMembers,
    scope: 'workspace',
    powerLevel: 30,
    labelKey: 'workspace_roles.permission.workspace_invite_members.label',
  },
  [PermissionId.WorkspaceRemoveMembers]: {
    id: PermissionId.WorkspaceRemoveMembers,
    scope: 'workspace',
    powerLevel: 40,
    labelKey: 'workspace_roles.permission.workspace_remove_members.label',
  },
  [PermissionId.WorkspaceBanMembers]: {
    id: PermissionId.WorkspaceBanMembers,
    scope: 'workspace',
    powerLevel: 50,
    labelKey: 'workspace_roles.permission.workspace_ban_members.label',
  },
  [PermissionId.WorkspaceManageRoles]: {
    id: PermissionId.WorkspaceManageRoles,
    scope: 'workspace',
    powerLevel: 60,
    labelKey: 'workspace_roles.permission.workspace_manage_roles.label',
  },
  [PermissionId.WorkspaceEditSettings]: {
    id: PermissionId.WorkspaceEditSettings,
    scope: 'workspace',
    powerLevel: 70,
    labelKey: 'workspace_roles.permission.workspace_edit_settings.label',
  },
  [PermissionId.WorkspaceCreateSubWorkspace]: {
    id: PermissionId.WorkspaceCreateSubWorkspace,
    scope: 'workspace',
    powerLevel: 80,
    labelKey: 'workspace_roles.permission.workspace_create_sub_workspace.label',
  },
  [PermissionId.WorkspaceDelete]: {
    id: PermissionId.WorkspaceDelete,
    scope: 'workspace',
    powerLevel: 98,
    labelKey: 'workspace_roles.permission.workspace_delete.label',
  },

  [PermissionId.ChatInviteMembers]: {
    id: PermissionId.ChatInviteMembers,
    scope: 'chat',
    powerLevel: 10,
    labelKey: 'workspace_roles.permission.chat_invite_members.label',
  },
  [PermissionId.ChatCreateInstantCall]: {
    id: PermissionId.ChatCreateInstantCall,
    scope: 'chat',
    powerLevel: 20,
    labelKey: 'workspace_roles.permission.chat_create_instant_call.label',
  },
  [PermissionId.ChatScheduleMeeting]: {
    id: PermissionId.ChatScheduleMeeting,
    scope: 'chat',
    powerLevel: 25,
    labelKey: 'workspace_roles.permission.chat_schedule_meeting.label',
  },
  [PermissionId.ChatManageSettings]: {
    id: PermissionId.ChatManageSettings,
    scope: 'chat',
    powerLevel: 30,
    labelKey: 'workspace_roles.permission.chat_manage_settings.label',
  },
  [PermissionId.ChatDeleteOthersMessages]: {
    id: PermissionId.ChatDeleteOthersMessages,
    scope: 'chat',
    powerLevel: 50,
    labelKey: 'workspace_roles.permission.chat_delete_others_messages.label',
  },
  [PermissionId.ChatRemoveMember]: {
    id: PermissionId.ChatRemoveMember,
    scope: 'chat',
    powerLevel: 60,
    labelKey: 'workspace_roles.permission.chat_remove_member.label',
  },
  [PermissionId.ChatBanMember]: {
    id: PermissionId.ChatBanMember,
    scope: 'chat',
    powerLevel: 70,
    labelKey: 'workspace_roles.permission.chat_ban_member.label',
  },
  [PermissionId.ChatDelete]: {
    id: PermissionId.ChatDelete,
    scope: 'chat',
    powerLevel: 85,
    labelKey: 'workspace_roles.permission.chat_delete.label',
  },
  [PermissionId.ChatReassignManager]: {
    id: PermissionId.ChatReassignManager,
    scope: 'chat',
    powerLevel: 90,
    labelKey: 'workspace_roles.permission.chat_reassign_manager.label',
  },

  [PermissionId.MeetingInviteUsers]: {
    id: PermissionId.MeetingInviteUsers,
    scope: 'meeting',
    powerLevel: 10,
    labelKey: 'workspace_roles.permission.meeting_invite_users.label',
  },
  [PermissionId.MeetingStartEarly]: {
    id: PermissionId.MeetingStartEarly,
    scope: 'meeting',
    powerLevel: 20,
    labelKey: 'workspace_roles.permission.meeting_start_early.label',
  },
  [PermissionId.MeetingToggleWaitingList]: {
    id: PermissionId.MeetingToggleWaitingList,
    scope: 'meeting',
    powerLevel: 25,
    labelKey: 'workspace_roles.permission.meeting_toggle_waiting_list.label',
  },
  [PermissionId.MeetingEditDetails]: {
    id: PermissionId.MeetingEditDetails,
    scope: 'meeting',
    powerLevel: 30,
    labelKey: 'workspace_roles.permission.meeting_edit_details.label',
  },
  [PermissionId.MeetingManageBreakoutRooms]: {
    id: PermissionId.MeetingManageBreakoutRooms,
    scope: 'meeting',
    powerLevel: 40,
    labelKey: 'workspace_roles.permission.meeting_manage_breakout_rooms.label',
  },
  [PermissionId.MeetingMuteParticipants]: {
    id: PermissionId.MeetingMuteParticipants,
    scope: 'meeting',
    powerLevel: 45,
    labelKey: 'workspace_roles.permission.meeting_mute_participants.label',
  },
  [PermissionId.MeetingManageConferenceMode]: {
    id: PermissionId.MeetingManageConferenceMode,
    scope: 'meeting',
    powerLevel: 50,
    labelKey: 'workspace_roles.permission.meeting_manage_conference_mode.label',
  },
  [PermissionId.MeetingKickUsers]: {
    id: PermissionId.MeetingKickUsers,
    scope: 'meeting',
    powerLevel: 70,
    labelKey: 'workspace_roles.permission.meeting_kick_users.label',
  },
  [PermissionId.MeetingBanUsers]: {
    id: PermissionId.MeetingBanUsers,
    scope: 'meeting',
    powerLevel: 80,
    labelKey: 'workspace_roles.permission.meeting_ban_users.label',
  },
  [PermissionId.MeetingCancelScheduled]: {
    id: PermissionId.MeetingCancelScheduled,
    scope: 'meeting',
    powerLevel: 85,
    labelKey: 'workspace_roles.permission.meeting_cancel_scheduled.label',
  },
  [PermissionId.MeetingReassignManager]: {
    id: PermissionId.MeetingReassignManager,
    scope: 'meeting',
    powerLevel: 90,
    labelKey: 'workspace_roles.permission.meeting_reassign_manager.label',
  },
};

export type RoomCreationScope = 'chat' | 'meeting';

export type ScopedRoomActionPowerLevels = {
  invite: number;
  kick: number;
  ban: number;
  redact?: number;
};

export const getScopedRoomActionPowerLevels = (scope: RoomCreationScope): ScopedRoomActionPowerLevels => {
  if (scope === 'chat') {
    return {
      invite: workspacesConfig[PermissionId.ChatInviteMembers].powerLevel,
      kick: workspacesConfig[PermissionId.ChatRemoveMember].powerLevel,
      ban: workspacesConfig[PermissionId.ChatBanMember].powerLevel,
      redact: workspacesConfig[PermissionId.ChatDeleteOthersMessages].powerLevel,
    };
  }

  return {
    invite: workspacesConfig[PermissionId.MeetingInviteUsers].powerLevel,
    kick: workspacesConfig[PermissionId.MeetingKickUsers].powerLevel,
    ban: workspacesConfig[PermissionId.MeetingBanUsers].powerLevel,
  };
};
